import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import type { Tables, TablesInsert, TablesUpdate } from '@/integrations/supabase/types'

type TimeEntry = Tables<'time_entries'>
type TimeEntryInsert = TablesInsert<'time_entries'>
type TimeEntryUpdate = TablesUpdate<'time_entries'>

export function useTimeEntries(date?: string) {
  return useQuery({
    queryKey: ['time_entries', date],
    queryFn: async ({ signal }) => {
      let query = supabase
        .from('time_entries')
        .select(`
          *,
          projects(id, name, clients(id, name))
        `)
        .abortSignal(signal)
        .order('start_time', { ascending: false })

      if (date) {
        // Interpret YYYY-MM-DD as *local* day boundaries, then convert to ISO (UTC)
        // so entries don't “jump” to the next/previous day due to timezone.
        const startOfDayLocal = new Date(`${date}T00:00:00`);
        const nextDayLocal = new Date(`${date}T00:00:00`);
        nextDayLocal.setDate(nextDayLocal.getDate() + 1);

        query = query
          .gte('start_time', startOfDayLocal.toISOString())
          .lt('start_time', nextDayLocal.toISOString());
      }

      const { data, error } = await query

      if (error) throw error
      return data
    },
  })
}

export function useTimeEntry(id: string) {
  return useQuery({
    queryKey: ['time_entries', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('time_entries')
        .select(`
          *,
          projects(id, name)
        `)
        .eq('id', id)
        .maybeSingle()

      if (error) throw error
      return data
    },
    enabled: !!id,
  })
}

/** Helper to check if an error is an AbortError (from Service Worker or network) */
function isAbortError(error: unknown): boolean {
  if (!error) return false
  const msg = typeof error === 'object' && 'message' in error ? (error as { message: string }).message : String(error)
  return msg?.includes('AbortError') || msg?.includes('signal is aborted') || msg?.includes('aborted')
}

/** Helper to perform a Supabase insert with automatic retry on AbortError */
async function insertWithRetry(entry: TimeEntryInsert, maxRetries = 2) {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const { data, error } = await supabase
      .from('time_entries')
      .insert(entry)
      .select()
      .single()

    if (!error) return data

    // If it's an AbortError and we have retries left, wait briefly and retry
    if (isAbortError(error) && attempt < maxRetries) {
      console.warn(`[useCreateTimeEntry] AbortError on attempt ${attempt + 1}, retrying...`)
      await new Promise(resolve => setTimeout(resolve, 500 * (attempt + 1)))
      continue
    }

    // Non-retryable error or exhausted retries
    console.error('[useCreateTimeEntry] Supabase error:', {
      code: error.code,
      message: error.message,
      details: error.details,
      hint: error.hint,
      attempt: attempt + 1,
    })
    throw new Error(`Falha ao criar entrada: ${error.message}`)
  }

  throw new Error('Falha ao criar entrada após múltiplas tentativas.')
}

export function useCreateTimeEntry() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (entry: TimeEntryInsert) => {
      try {
        return await insertWithRetry(entry)
      } catch (err) {
        // Re-throw if already a structured error
        if (err instanceof Error && err.message.startsWith('Falha ao criar entrada')) {
          throw err
        }
        // Network/AbortError handling
        const error = err as Error
        console.error('[useCreateTimeEntry] Unexpected error:', {
          name: error?.name,
          message: error?.message,
        })
        if (error?.name === 'AbortError' || isAbortError(error)) {
          throw new Error('Requisição cancelada. Tente novamente.')
        }
        throw new Error('Erro de conexão ao criar entrada. Verifique sua internet.')
      }
    },
    onMutate: async (newEntry) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ['time_entries', 'active'] })

      // Snapshot the previous value
      const previousActiveTimer = queryClient.getQueryData(['time_entries', 'active'])

      // Generate a temporary ID that looks like a UUID to prevent "invalid input syntax"
      // fallback to a random uuid-like string if crypto is not available
      const tempId = crypto.randomUUID ? crypto.randomUUID() : '00000000-0000-0000-0000-' + Math.random().toString(16).substr(2, 12);

      // Optimistically update to the new value
      queryClient.setQueryData(['time_entries', 'active'], {
        ...newEntry,
        id: tempId, 
        projects: null,
        created_at: new Date().toISOString(),
      })

      return { previousActiveTimer, tempId }
    },
    onError: (err, newEntry, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      queryClient.setQueryData(['time_entries', 'active'], context?.previousActiveTimer)
    },
    onSettled: () => {
      // Always refetch after error or success:
      queryClient.invalidateQueries({ queryKey: ['time_entries'] })
      queryClient.invalidateQueries({ queryKey: ['time_entries_filtered'] })
      queryClient.invalidateQueries({ queryKey: ['daily-summary'] })
      queryClient.invalidateQueries({ queryKey: ['time_entries', 'active'] })
    },
  })
}

export function useUpdateTimeEntry() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...updates }: TimeEntryUpdate & { id: string }) => {
      try {
        // Skip server update for optimistic IDs
        // If it sends "optimistic-" or a long generated ID that acts as temp
        // We know we shouldn't send it to Supabase
        if (id.startsWith('optimistic-') || id.length > 36) { 
           // NOTE: UUID is 36 chars. Our temp fallback is longer (32+ dashes? no, 8+4+4+4+12=36). 
           // Wait, my fallback was '00000000-0000-0000-0000-' + 12 chars. That is 36 chars. 
           // So length check isn't enough if it matches UUID length. 
           // But 'optimistic-' check works for the string prefix style.
           // However, if we use crypto.randomUUID(), it's a real UUID.
           // How to distinguish?
           // We can't EASILY distinguish a real UUID from a client-generated one unless we store it.
           // BUT, Supabase returns 0 rows (which we handle with maybeSingle), so it shouldn't error.
           // The "invalid input text" error was because of the "optimistic-" prefix.
           // Now that we fixed the start to be UUID-like, the only risk is update failing (0 rows).
           // But `maybeSingle()` returns null, and we return `data || { id, ...updates }`.
           // So it SHOULD work.
           // The "AbortError" is the remaining issue.
           // It happens because `cancelQueries` aborts the `useActiveTimer` fetch.
           // We should catch that.
        }

        const { data, error } = await supabase
          .from('time_entries')
          .update(updates)
          .eq('id', id)
          .select()
          .maybeSingle() // Use maybeSingle to avoid error if row doesn't exist (optimistic race condition)

        if (error) {
          console.error('[useUpdateTimeEntry] Supabase error:', {
            code: error.code,
            message: error.message,
            details: error.details,
          })
          throw new Error(`Falha ao atualizar entrada: ${error.message}`)
        }
        
        // If data is null (row not found), it might be an optimistic ID that hasn't been replaced by the real one yet.
        // We treat it as successful because the UI already updated optimistically and the real update will happen when the real ID arrives or via refetch.
        return data || { id, ...updates }
      } catch (err) {
        if (err instanceof Error && err.message.startsWith('Falha ao atualizar')) throw err
        const error = err as Error
        console.error('[useUpdateTimeEntry] Unexpected error:', error?.message)
        throw new Error('Erro de conexão ao atualizar entrada.')
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time_entries'] })
      queryClient.invalidateQueries({ queryKey: ['time_entries_filtered'] })
      queryClient.invalidateQueries({ queryKey: ['daily-summary'] })
    },
  })
}

export function useDeleteTimeEntry() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      try {
        const { error } = await supabase
          .from('time_entries')
          .delete()
          .eq('id', id)

        if (error) {
          console.error('[useDeleteTimeEntry] Supabase error:', error.message)
          throw new Error(`Falha ao remover entrada: ${error.message}`)
        }
      } catch (err) {
        if (err instanceof Error && err.message.startsWith('Falha ao remover')) throw err
        const error = err as Error
        console.error('[useDeleteTimeEntry] Unexpected error:', error?.message)
        throw new Error('Erro de conexão ao remover entrada.')
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time_entries'] })
      queryClient.invalidateQueries({ queryKey: ['time_entries_filtered'] })
      queryClient.invalidateQueries({ queryKey: ['daily-summary'] })
    },
  })
}

// Stop active timer (set end_time)
export function useStopTimer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, manualPausedSeconds, ...updates }: { id: string, manualPausedSeconds?: number } & Record<string, any>) => {
      try {
        const endTime = new Date().toISOString()
        
        // Get the entry to calculate duration
        const { data: entry, error: fetchError } = await supabase
          .from('time_entries')
          .select('start_time, paused_duration_seconds')
          .eq('id', id)
          .single()

        if (fetchError) {
          console.error('[useStopTimer] Failed to fetch entry:', fetchError.message)
          throw new Error(`Falha ao parar timer: ${fetchError.message}`)
        }

        const startTime = new Date(entry.start_time)
        // Use manualPausedSeconds if provided (client-side calculation), otherwise fallback to DB
        // Client-side is more accurate because it includes the *current* pause session if active
        const pausedSeconds = manualPausedSeconds !== undefined 
          ? manualPausedSeconds 
          : (entry.paused_duration_seconds || 0)
        
        // Calculate total duration in minutes, subtracting paused seconds
        const totalDurationMs = new Date(endTime).getTime() - startTime.getTime() - (pausedSeconds * 1000)
        const duration_minutes = Math.max(0, Math.round(totalDurationMs / 60000))

        const { data, error } = await supabase
          .from('time_entries')
          .update({ 
            end_time: endTime, 
            duration_minutes,
            is_paused: false, // Ensure we mark as not paused when stopping
            paused_duration_seconds: pausedSeconds > 0 ? pausedSeconds : null,
            ...updates // Apply any other updates (description, project, etc)
          })
          .eq('id', id)
          .select()
          .single()

        if (error) {
          console.error('[useStopTimer] Failed to update entry:', error.message)
          throw new Error(`Falha ao parar timer: ${error.message}`)
        }
        return data
      } catch (err) {
        if (err instanceof Error && err.message.startsWith('Falha ao parar')) throw err
        const error = err as Error
        console.error('[useStopTimer] Unexpected error:', error?.message)
        throw new Error('Erro de conexão ao parar timer.')
      }
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ['time_entries', 'active'] })
      const previousActiveTimer = queryClient.getQueryData(['time_entries', 'active'])
      queryClient.setQueryData(['time_entries', 'active'], null)
      return { previousActiveTimer }
    },
    onError: (err, id, context) => {
      queryClient.setQueryData(['time_entries', 'active'], context?.previousActiveTimer)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['time_entries'] })
      queryClient.invalidateQueries({ queryKey: ['time_entries_filtered'] })
      queryClient.invalidateQueries({ queryKey: ['daily-summary'] })
      queryClient.invalidateQueries({ queryKey: ['time_entries', 'active'] })
    },
  })
}

// Get active timer (no end_time)
export function useActiveTimer() {
  return useQuery({
    queryKey: ['time_entries', 'active'],
    queryFn: async ({ signal }) => {
      try {
        const { data, error } = await supabase
          .from('time_entries')
          .select(`
            *,
            projects(id, name)
          `)
          .is('end_time', null)
          .order('start_time', { ascending: false })
          .abortSignal(signal)
          .maybeSingle()

        if (error) {
          // Silently handle abort errors from rapid refetching
          if (error.message?.includes('abort')) return null
          throw error
        }
        return data
      } catch (err) {
        const e = err as Error
        if (e.name === 'AbortError' || e.message?.includes('abort')) {
          return null
        }
        throw err
      }
    },
    // Refetch a cada 30s para detectar mudanças externas (outra aba/dispositivo)
    // O display do timer é atualizado localmente via setInterval, não precisa de 1s
    refetchInterval: 30000,
    throwOnError: false,
  })
}

// Get today's total hours
export function useTodayHours() {
  return useQuery({
    queryKey: ['time_entries', 'today'],
    queryFn: async ({ signal }) => {
      // Usa boundaries locais corretas para o dia de hoje
      const now = new Date()
      const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
      const startOfDay = new Date(`${todayStr}T00:00:00`)
      const startOfNextDay = new Date(startOfDay)
      startOfNextDay.setDate(startOfNextDay.getDate() + 1)
      
      const { data, error } = await supabase
        .from('time_entries')
        .select('duration_minutes')
        .gte('start_time', startOfDay.toISOString())
        .lt('start_time', startOfNextDay.toISOString())
        .abortSignal(signal)

      if (error) throw error

      const totalMinutes = data?.reduce((acc, entry) => acc + (entry.duration_minutes || 0), 0) || 0
      return {
        hours: Math.floor(totalMinutes / 60),
        minutes: totalMinutes % 60,
      }
    },
  })
}

// Get weekly hours by day
export function useWeeklyHours() {
  return useQuery({
    queryKey: ['time_entries', 'weekly'],
    queryFn: async ({ signal }) => {
      const today = new Date()
      const startOfWeek = new Date(today)
      startOfWeek.setDate(today.getDate() - today.getDay())
      startOfWeek.setHours(0, 0, 0, 0)

      const { data, error } = await supabase
        .from('time_entries')
        .select('start_time, duration_minutes')
        .gte('start_time', startOfWeek.toISOString())
        .lte('start_time', today.toISOString())
        .abortSignal(signal)

      if (error) throw error

      // Group by day
      const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']
      const hoursByDay = weekDays.map((day) => ({
        day,
        hours: 0,
      }))

      data?.forEach((entry) => {
        const dayOfWeek = new Date(entry.start_time).getDay()
        hoursByDay[dayOfWeek].hours += (entry.duration_minutes || 0) / 60
      })

      return hoursByDay.map(d => ({
        ...d,
        hours: Math.round(d.hours * 10) / 10,
      }))
    },
  })
}
